import { useEffect, useMemo, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { gmailThreadService } from '../../services/gmailThreadService';
import type { GmailThreadDto, GmailThreadMessageDto } from '../../types/quotation.types';
import { formatDateTime12h } from '../../utils/formatRelative';
import { parseSenderName } from '../../utils/senderName';
import { cn } from '@/lib/utils';

interface GmailThreadViewProps {
  threadId: string;
}

// CSC-294: Conversation tab body. Pulls the full Gmail thread via gmailThreadService —
// hits the warm cache instantly when sales hovered the master-list row first. Each
// message renders flat (no accordion) so the whole conversation is visible at a glance,
// matching how sales reads the email in Gmail's expanded thread view.
export function GmailThreadView({ threadId }: GmailThreadViewProps) {
  const [thread, setThread] = useState<GmailThreadDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    gmailThreadService
      .get(threadId)
      .then((data) => {
        if (cancelled) return;
        setThread(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  if (loading && !thread) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading conversation…
      </div>
    );
  }

  if (error && !thread) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!thread || thread.messages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm italic text-muted-foreground">
        No messages in this conversation.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col bg-muted/30">
      {/* min-h-0 + flex-1 on the parent + overflow-y-auto on the ul = the gray
          background fills the full panel height while only the message list
          scrolls when the conversation is taller than the pane. */}
      <ul className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {thread.messages.map((msg, idx) => (
          <ThreadMessageCard key={msg.messageId || idx} message={msg} />
        ))}
      </ul>
    </div>
  );
}

interface ThreadMessageCardProps {
  message: GmailThreadMessageDto;
}

function ThreadMessageCard({ message }: ThreadMessageCardProps) {
  const senderLabel = useMemo(
    () => parseSenderName(message.from) || message.from || '(unknown sender)',
    [message.from],
  );
  const dateLabel = useMemo(() => formatDateTime12h(message.date), [message.date]);

  return (
    <li className="rounded-md border border-border bg-background shadow-sm">
      {/* Compact header strip — sender + date + inbound/outbound chip. No collapse
          toggle: the body always renders so the whole conversation is in one scroll. */}
      <div className="flex items-center gap-2 border-b px-3 py-2 text-sm">
        <Mail
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            message.isInbound ? 'text-primary' : 'text-muted-foreground',
          )}
          aria-label={message.isInbound ? 'Received' : 'Sent'}
        />
        <span className="min-w-0 flex-1 truncate font-medium">{senderLabel}</span>
        <span className="shrink-0 text-xs text-muted-foreground" title={dateLabel}>
          {dateLabel}
        </span>
      </div>
      <div className="px-3 py-2.5">
        {/* Address block — kept compact so the body remains the focus. Subject is
            usually duplicated across replies in a thread; only render it on the first
            message via the parent's filtering if we ever decide to suppress repeats. */}
        <div className="mb-2 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          <div className="truncate"><span className="font-medium">From:</span> {message.from || '—'}</div>
          <div className="truncate"><span className="font-medium">To:</span> {message.to || '—'}</div>
          {message.cc && <div className="truncate"><span className="font-medium">Cc:</span> {message.cc}</div>}
          {message.subject && <div className="truncate"><span className="font-medium">Subject:</span> {message.subject}</div>}
        </div>
        <MessageBody message={message} />
      </div>
    </li>
  );
}

function MessageBody({ message }: { message: GmailThreadMessageDto }) {
  // Backend already passed bodyHtml through Ganss.Xss before serializing — script,
  // iframe, on* handlers, javascript: URLs, and <img> tags are stripped. This is the
  // documented trust boundary; rendering raw HTML downstream is what makes the
  // viewer feel like Gmail. If the source had no HTML we fall back to plain text
  // rendered via a <pre>-style block so line breaks survive.
  if (message.bodyHtml) {
    return (
      <div
        className="gmail-thread-body max-w-full overflow-x-auto break-words text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
      />
    );
  }
  if (message.bodyText) {
    return (
      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground">
        {message.bodyText}
      </pre>
    );
  }
  return (
    <div className="text-sm italic text-muted-foreground">
      (No body content)
    </div>
  );
}
